import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { useAuth } from '../app/auth-context'
import { Button, Card, ErrorNote, TextInput } from '../components/ui'
import { ApiError } from '../lib/api'
import { formatJalaliDateTime, formatMobile, toLatinDigits, toPersianDigits } from '../lib/fa'
import {
  createStaff,
  listStaff,
  ROLE_LABEL,
  updateStaff,
  type StaffDto,
  type StaffRole,
} from '../lib/staff-api'

const STAFF_KEY = ['staff'] as const

const messageOf = (error: unknown): string =>
  error instanceof ApiError ? error.messageFa : 'مشکلی پیش آمد. دوباره تلاش کنید.'

/** Who can sign in to the backoffice. Admins only — the API enforces it, the menu hides it. */
export function StaffPage() {
  const staff = useQuery({ queryKey: STAFF_KEY, queryFn: listStaff })

  return (
    <div className="max-w-5xl">
      <h1 className="mb-1 text-2xl font-bold">کارکنان</h1>
      <p className="mb-6 text-sm text-muted">
        فقط شماره‌های فعال این فهرست می‌توانند وارد پیشخوان شوند.
      </p>

      <AddStaffForm />

      <Card className="mt-6 overflow-x-auto p-0">
        {staff.isPending ? (
          <p className="p-5 text-sm text-muted">در حال بارگذاری…</p>
        ) : staff.isError ? (
          <div className="p-5">
            <ErrorNote>{messageOf(staff.error)}</ErrorNote>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-line text-right text-muted">
              <tr>
                <th className="px-4 py-3 font-medium">نام</th>
                <th className="px-4 py-3 font-medium">موبایل</th>
                <th className="px-4 py-3 font-medium">نقش</th>
                <th className="px-4 py-3 font-medium">وضعیت</th>
                <th className="px-4 py-3 font-medium">آخرین ورود</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {staff.data.map((member) => (
                <StaffRow key={member.id} member={member} />
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  )
}

function AddStaffForm() {
  const queryClient = useQueryClient()
  const [mobile, setMobile] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState<StaffRole>('OPERATOR')

  const create = useMutation({
    mutationFn: createStaff,
    onSuccess: () => {
      setMobile('')
      setName('')
      setRole('OPERATOR')
      void queryClient.invalidateQueries({ queryKey: STAFF_KEY })
    },
  })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    create.mutate({ mobile, name, role })
  }

  const fields = create.error instanceof ApiError ? create.error.fields : undefined

  return (
    <Card>
      <h2 className="mb-4 font-semibold">افزودن همکار</h2>
      <form onSubmit={submit} className="flex flex-wrap items-start gap-3">
        <TextInput
          label="نام"
          className="w-56"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={fields?.name}
        />
        <TextInput
          label="شماره موبایل"
          className="w-48"
          type="tel"
          inputMode="numeric"
          dir="ltr"
          placeholder="۰۹۱۲۳۴۵۶۷۸۹"
          value={toPersianDigits(mobile)}
          onChange={(e) => setMobile(toLatinDigits(e.target.value).replace(/\D/g, '').slice(0, 11))}
          error={fields?.mobile}
        />
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium">نقش</span>
          <RoleSelect value={role} onChange={setRole} />
        </label>
        <Button type="submit" className="mt-7" loading={create.isPending}>
          افزودن
        </Button>
      </form>
      {create.isError && !fields ? (
        <div className="mt-3">
          <ErrorNote>{messageOf(create.error)}</ErrorNote>
        </div>
      ) : null}
    </Card>
  )
}

function StaffRow({ member }: { member: StaffDto }) {
  const { staff: me } = useAuth()
  const queryClient = useQueryClient()
  const [editingName, setEditingName] = useState<string | null>(null)
  const isMe = me?.id === member.id

  const update = useMutation({
    mutationFn: (body: Parameters<typeof updateStaff>[1]) => updateStaff(member.id, body),
    onSuccess: () => {
      setEditingName(null)
      void queryClient.invalidateQueries({ queryKey: STAFF_KEY })
    },
  })

  return (
    <tr className={`border-b border-line last:border-0 ${member.isActive ? '' : 'text-muted'}`}>
      <td className="px-4 py-3">
        {editingName === null ? (
          <span className="font-medium">
            {member.name}
            {isMe ? <span className="ms-2 text-xs text-muted">(شما)</span> : null}
          </span>
        ) : (
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault()
              update.mutate({ name: editingName })
            }}
          >
            <input
              autoFocus
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              className="h-9 w-40 rounded-lg border border-line bg-card px-2 outline-none focus:border-brand-500"
            />
            <Button type="submit" className="h-9" loading={update.isPending}>
              ذخیره
            </Button>
            <Button type="button" variant="secondary" className="h-9" onClick={() => setEditingName(null)}>
              انصراف
            </Button>
          </form>
        )}
        {update.isError ? (
          <div className="mt-2">
            <ErrorNote>{messageOf(update.error)}</ErrorNote>
          </div>
        ) : null}
      </td>
      <td className="px-4 py-3" dir="ltr" style={{ textAlign: 'right' }}>
        {formatMobile(member.mobile)}
      </td>
      <td className="px-4 py-3">
        {isMe ? (
          ROLE_LABEL[member.role]
        ) : (
          <RoleSelect
            value={member.role}
            disabled={update.isPending}
            onChange={(role) => update.mutate({ role })}
          />
        )}
      </td>
      <td className="px-4 py-3">{member.isActive ? 'فعال' : 'غیرفعال'}</td>
      <td className="px-4 py-3 text-muted">
        {member.lastLoginAt ? formatJalaliDateTime(member.lastLoginAt) : 'هنوز وارد نشده'}
      </td>
      <td className="px-4 py-3">
        <div className="flex justify-end gap-2">
          {editingName === null ? (
            <Button variant="secondary" className="h-9" onClick={() => setEditingName(member.name)}>
              ویرایش نام
            </Button>
          ) : null}
          {isMe ? null : member.isActive ? (
            <Button
              variant="danger"
              className="h-9"
              disabled={update.isPending}
              onClick={() => {
                if (window.confirm(`دسترسی ${member.name} به پیشخوان قطع شود؟`)) {
                  update.mutate({ isActive: false })
                }
              }}
            >
              غیرفعال‌سازی
            </Button>
          ) : (
            <Button
              variant="secondary"
              className="h-9"
              disabled={update.isPending}
              onClick={() => update.mutate({ isActive: true })}
            >
              فعال‌سازی
            </Button>
          )}
        </div>
      </td>
    </tr>
  )
}

function RoleSelect({
  value,
  onChange,
  disabled,
}: {
  value: StaffRole
  onChange: (role: StaffRole) => void
  disabled?: boolean
}) {
  return (
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value as StaffRole)}
      className="h-10 rounded-xl border border-line bg-card px-3 outline-none focus:border-brand-500"
    >
      {(Object.keys(ROLE_LABEL) as StaffRole[]).map((role) => (
        <option key={role} value={role}>
          {ROLE_LABEL[role]}
        </option>
      ))}
    </select>
  )
}
